import UnitPageShell from '../components/shared/UnitPageShell';
import React from 'react';
import { Alert, Box, Button, Divider, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import QuizIcon from '@mui/icons-material/Quiz';
import { loadCustomQuizLinks } from '../services/quizEditorStorage';
import { useAuth } from '../context/AuthContext';
import { tokens, getCardSx } from '../theme/tokens';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';

interface QuizLink {
  id: string;
  title: string;
  route: string;
  module: string;
}

const QUIZ_LIST: QuizLink[] = [
  // Modul 1
  { id: 'M1_U1_X99', title: 'Quiz zu Modul 1 Einheit 1', route: '/units/m1_u1_x99_quiz', module: 'M1' },
  { id: 'M1_U2_X99', title: 'Quiz rund um die Lüftung', route: '/units/m1_u2_x99_quiz', module: 'M1' },
  { id: 'M1_U3_X99', title: 'Quiz zu Modul 1 Einheit 3', route: '/units/m1_u3_x99_quiz', module: 'M1' },
  // Modul 2
  { id: 'M2_U1_X99', title: 'Quiz zu Modul 2 Einheit 1', route: '/units/m2_u1_x99_quiz', module: 'M2' },
  { id: 'M2_U2_X99', title: 'Quiz zu Modul 2 Einheit 2', route: '/units/m2_u2_x99_quiz', module: 'M2' },
  // Modul 3
  { id: 'M3_U1_X99', title: 'Quiz zu Modul 3 Einheit 1', route: '/units/m3_u1_x99_quiz', module: 'M3' },
  { id: 'M3_U2_X99', title: 'Quiz zu Modul 3 Einheit 2', route: '/units/m3_u2_x99_quiz', module: 'M3' },
  // Modul 4
  { id: 'M4_U1_X99', title: 'Quiz zu Modul 4 Einheit 1', route: '/units/m4_u1_x99_quiz', module: 'M4' },
  { id: 'M4_U2_X99', title: 'Quiz zu Modul 4 Einheit 2', route: '/units/m4_u2_x99_quiz', module: 'M4' },
  { id: 'M4_U3_X99', title: 'Quiz zu Modul 4 Einheit 3', route: '/units/m4_u3_x99_quiz', module: 'M4' },
  { id: 'M4_U4_X99', title: 'Quiz zu Modul 4 Einheit 4', route: '/units/m4_u4_x99_quiz', module: 'M4' },
  // Modul 5
  { id: 'M5_U99_X99', title: 'Quiz zu Modul 5 Einheit 99', route: '/units/m5_u99_quiz', module: 'M5' },
  // Modul 6
  { id: 'M6_U1_X99', title: 'Quiz zu Modul 6 Einheit 1', route: '/units/m6_u1_x99_quiz', module: 'M6' },
  { id: 'M6_U2_X99', title: 'Quiz zu Modul 6 Einheit 2', route: '/units/m6_u2_x99_quiz', module: 'M6' },
  { id: 'M6_U3_X99', title: 'Quiz zu Modul 6 Einheit 3', route: '/units/m6_u3_x99_quiz', module: 'M6' },
  { id: 'M6_U4_X99', title: 'Quiz zu Modul 6 Einheit 4', route: '/units/m6_u4_x99_quiz', module: 'M6' },
  { id: 'M6_U5_X99', title: 'Quiz zu Modul 6 Einheit 5', route: '/units/m6_u5_x99_quiz', module: 'M6' },
  { id: 'M6_U6_X99', title: 'Quiz zu Modul 6 Einheit 6', route: '/units/m6_u6_x99_quiz', module: 'M6' },
  { id: 'M6_U7_X99', title: 'Quiz zu Modul 6 Einheit 7', route: '/units/m6_u7_x99_quiz', module: 'M6' },
];

const QuizzesOverview: React.FC = () => {
  const { darkMode } = useThemeContext();
  const { canAnswerQuizzes } = useAuth();
  const [customQuizzes, setCustomQuizzes] = React.useState<QuizLink[]>([]);

  React.useEffect(() => {
    setCustomQuizzes(loadCustomQuizLinks() as QuizLink[]);
  }, []);

  const combinedQuizzes = React.useMemo(() => {
    const map: Map<string, QuizLink> = new Map();
    QUIZ_LIST.forEach((quiz) => map.set(quiz.id, quiz));
    customQuizzes.forEach((quiz) => {
      if (!map.has(quiz.id)) map.set(quiz.id, quiz);
    });
    return Array.from(map.values());
  }, [customQuizzes]);
  // Group quizzes by module
  const groupedQuizzes = React.useMemo(() => {
    const groups: Record<string, QuizLink[]> = {};
    combinedQuizzes.forEach(quiz => {
      if (!groups[quiz.module]) groups[quiz.module] = [];
      groups[quiz.module].push(quiz);
    });
    return groups;
  }, [combinedQuizzes]);

  const modules = Object.keys(groupedQuizzes).sort();

  if (!canAnswerQuizzes) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Paper sx={{ ...getCardSx(darkMode), p: 3, mx: { xs: 1, md: 2 } }}>
          <Alert severity="error">Keine Berechtigung fuer Quizze.</Alert>
        </Paper>
      </UnitPageShell>
    );
  }

  return React.createElement(
    UnitPageShell,
    { title: 'Quiz-Übersicht' },
    React.createElement(
      Paper,
      { sx: { p: { xs: 2, md: 3 }, mx: { xs: 1, md: 2 }, width: 'auto' } },
      React.createElement(
        Typography,
        { variant: 'h4', gutterBottom: true, sx: { color: 'primary.main', fontWeight: 600 } },
        'Quiz-Übersicht'
      ),
      React.createElement(
        Typography,
        { variant: 'body1', gutterBottom: true, sx: { mb: 3 } },
        'Wähle ein Quiz aus, um dein Wissen zu testen. Alle Quizze laden ihre Fragen direkt aus der Quiz.xlsx Datei.'
      ),
      React.createElement(
        React.Fragment,
        null,
        ...modules.map((moduleId) =>
          React.createElement(
            Box,
            { key: moduleId, sx: { mb: 4 } },
            React.createElement(
              Typography,
              { variant: 'h6', sx: { color: 'primary.main', mb: 1, fontWeight: 600 } },
              `Modul ${moduleId}`
            ),
            React.createElement(Divider, { sx: { mb: 2 } }),
            React.createElement(
              List,
              { sx: { pt: 0 } },
              groupedQuizzes[moduleId].map((quiz) =>
                React.createElement(
                  ListItem,
                  {
                    key: quiz.id,
                    sx: { borderRadius: 1, mb: 1, '&:hover': { bgcolor: 'action.hover' } },
                    secondaryAction: React.createElement(
                      RouterLink,
                      { to: quiz.route, style: { textDecoration: 'none' } },
                      React.createElement(
                        Button,
                        { variant: 'contained', size: 'small', startIcon: React.createElement(QuizIcon, null) },
                        'Quiz starten'
                      )
                    )
                  },
                  React.createElement(ListItemText, { primary: quiz.title, secondary: quiz.id, primaryTypographyProps: { fontWeight: 500 } })
                )
              )
            )
          )
        )
      )
    )
  );
};

export default QuizzesOverview;
